/**
 * @module creativeWorksSearchMappers
 * Maps raw catalog JSON (search hits and item detail) into {@link CreativeWorksSearchCard}
 * and {@link CreativeWorksSearchItem} shapes used by the copyright search UI.
 */

import { CREATIVE_WORKS_SEARCH_ITEM_AT } from 'api/creativeWorksSearch/constants';

import {
  extractCopyrightRegistrationDate,
  extractItemIdFromSearchRow,
  firstAbsolutizedImage,
  firstPlainLine,
  formatCreativeWorksSearchCardTitle,
  joinFormatFragments,
  catalogItemUrl,
  controlNumberFromCatalogItem,
  plainTextSanitize,
  plainTextSanitizeArray,
  urlAbsolutize,
} from './creativeWorksSearchParseUtils';

import type {
  CreativeWorksCitations,
  CreativeWorksContributorDetail,
  CreativeWorksDigitalResource,
  CreativeWorksSearchItem,
  CreativeWorksOtherFormat,
  CreativeWorksSearchCard,
  CreativeWorksSubjectEntry,
} from './interfaces';
import type {
  CreativeWorksCatalogItemRaw,
  CreativeWorksCatalogCiteThisRaw,
  CreativeWorksCatalogItemApiResponse,
  CreativeWorksCatalogResourceRaw,
  CreativeWorksCatalogResultRaw,
} from 'api/creativeWorksSearch/interfaces';

const MAX_RESOURCE_PREVIEW = 25;

const toArray = (value: unknown): unknown[] =>
  Array.isArray(value) ? value : value != null && value !== '' ? [value] : [];
const ONLINE_FORMAT_ORDER = [
  'pdf',
  'video',
  'audio',
  'image',
  'online text',
  'web page',
] as const;

const normalizeContributors = (
  raw: unknown
): CreativeWorksContributorDetail[] => {
  if (!raw) {
    return [];
  }

  if (typeof raw === 'string') {
    const name = plainTextSanitize(raw);
    return name ? [{ name }] : [];
  }

  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const record = raw as Record<string, unknown>;
    const titledName = plainTextSanitize(record.title || record.name);
    if (titledName) {
      return [
        {
          name: titledName,
          url: typeof record.url === 'string' ? record.url : undefined,
        },
      ];
    }
    return Object.entries(record).map(([name, url]) => ({
      name: plainTextSanitize(name),
      url: typeof url === 'string' ? url : undefined,
    }));
  }

  if (!Array.isArray(raw)) {
    return [];
  }

  const mapped: CreativeWorksContributorDetail[] = [];
  for (const entry of raw) {
    if (typeof entry === 'string') {
      const name = plainTextSanitize(entry);
      if (name) {
        mapped.push({ name });
      }
      continue;
    }
    if (!entry || typeof entry !== 'object') {
      continue;
    }
    const record = entry as Record<string, unknown>;
    const titledName = plainTextSanitize(record.title || record.name);
    if (titledName) {
      mapped.push({
        name: titledName,
        url: typeof record.url === 'string' ? record.url : undefined,
      });
      continue;
    }
    for (const [name, url] of Object.entries(record)) {
      const sanitizedName = plainTextSanitize(name);
      if (sanitizedName) {
        mapped.push({
          name: sanitizedName,
          url: typeof url === 'string' ? url : undefined,
        });
      }
    }
  }
  return mapped;
};

const normalizeSubjects = (raw: unknown): CreativeWorksSubjectEntry[] => {
  if (!raw) {
    return [];
  }

  if (typeof raw === 'string') {
    const title = plainTextSanitize(raw);
    return title ? [{ title }] : [];
  }

  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const record = raw as Record<string, unknown>;
    const title = plainTextSanitize(record.title || record.name);
    if (title) {
      return [
        {
          title,
          url: typeof record.url === 'string' ? record.url : undefined,
        },
      ];
    }
    return Object.entries(record).map(([title, url]) => ({
      title: plainTextSanitize(title),
      url: typeof url === 'string' ? url : undefined,
    }));
  }

  if (!Array.isArray(raw)) {
    return [];
  }

  const mapped: CreativeWorksSubjectEntry[] = [];
  for (const entry of raw) {
    if (typeof entry === 'string') {
      const title = plainTextSanitize(entry);
      if (title) {
        mapped.push({ title });
      }
      continue;
    }
    if (!entry || typeof entry !== 'object') {
      continue;
    }
    const record = entry as Record<string, unknown>;
    if (
      Object.keys(record).length === 1 &&
      typeof Object.values(record)[0] === 'string'
    ) {
      const title = plainTextSanitize(Object.keys(record)[0]);
      const url = Object.values(record)[0] as string;
      if (title) {
        mapped.push({ title, url });
      }
      continue;
    }
    const title = plainTextSanitize(record.title || record.name);
    if (title) {
      mapped.push({
        title,
        url: typeof record.url === 'string' ? record.url : undefined,
      });
    }
  }
  return mapped;
};

const normalizePartofCollections = (
  raw: unknown
): CreativeWorksSubjectEntry[] => {
  const rows = Array.isArray(raw) ? raw : raw != null ? [raw] : [];
  const mapped: CreativeWorksSubjectEntry[] = [];
  for (const entry of rows) {
    if (entry && typeof entry === 'object') {
      const record = entry as Record<string, unknown>;
      const title = plainTextSanitize(record.title);
      if (!title) {
        continue;
      }
      mapped.push({
        title,
        url: typeof record.url === 'string' ? record.url : undefined,
      });
      continue;
    }
    const title = plainTextSanitize(entry);
    if (title) {
      mapped.push({ title });
    }
  }
  return mapped;
};

const mergeSubjects = (
  item: CreativeWorksCatalogItemRaw
): CreativeWorksSubjectEntry[] => {
  const merged = [
    ...normalizeSubjects(item.subjects),
    ...normalizeSubjects(item.subject),
    ...normalizePartofCollections(item.partof),
  ];
  const seen = new Set<string>();
  return merged.filter((entry) => {
    const key = entry.title?.toLowerCase() || '';
    if (!key || seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

const mimetypeFromNestedFiles = (files: unknown): string | undefined => {
  if (!Array.isArray(files)) {
    return undefined;
  }
  for (const group of files) {
    const entries = Array.isArray(group) ? group : [group];
    for (const entry of entries) {
      if (entry && typeof entry === 'object') {
        const mime = plainTextSanitize(
          (entry as { mimetype?: string }).mimetype
        );
        if (mime) {
          return mime;
        }
      }
    }
  }
  return undefined;
};

/**
 * Normalizes a single catalog `resources` entry for the detail view.
 * @param resource - Raw resource object from catalog item JSON.
 * @returns Sanitized {@link CreativeWorksDigitalResource}, or `null` when empty after mapping.
 */
export const mapLocResource = (
  resource: CreativeWorksCatalogResourceRaw
): CreativeWorksDigitalResource | null => {
  const mime =
    plainTextSanitize(resource.mimetype) ||
    mimetypeFromNestedFiles(resource.files);
  const normalized: CreativeWorksDigitalResource = {
    url: typeof resource.url === 'string' ? resource.url : undefined,
    mimetype: mime || undefined,
    size: typeof resource.size === 'number' ? resource.size : undefined,
    height: typeof resource.height === 'number' ? resource.height : undefined,
    width: typeof resource.width === 'number' ? resource.width : undefined,
    files: resource.files,
    image: urlAbsolutize(resource.image) || undefined,
    pdf: urlAbsolutize(resource.pdf) || undefined,
    fulltext_file: urlAbsolutize(resource.fulltext_file) || undefined,
    video: urlAbsolutize(resource.video) || undefined,
    video_stream: urlAbsolutize(resource.video_stream) || undefined,
    closed_captions: urlAbsolutize(resource.closed_captions) || undefined,
  };

  const hasContent = Object.values(normalized).some(
    (value) => value !== undefined && value !== null && value !== ''
  );
  return hasContent ? normalized : null;
};

const normalizeResources = (
  data: CreativeWorksCatalogItemApiResponse,
  item: CreativeWorksCatalogItemRaw
): CreativeWorksDigitalResource[] => {
  const rows = Array.isArray(data.resources)
    ? data.resources
    : Array.isArray(item.resources)
    ? item.resources
    : [];

  return rows
    .map((row) => (row ? mapLocResource(row) : null))
    .filter((row): row is CreativeWorksDigitalResource => row !== null);
};

const normalizedOtherFormats = (
  item: CreativeWorksCatalogItemRaw
): CreativeWorksOtherFormat[] => {
  if (!Array.isArray(item.other_formats)) {
    return [];
  }
  const mapped: CreativeWorksOtherFormat[] = [];
  for (const entry of item.other_formats) {
    if (entry && typeof entry === 'object') {
      const record = entry as Record<string, unknown>;
      const label = plainTextSanitize(record.label);
      const url = urlAbsolutize(record.link || record.url);
      if (label || url) {
        mapped.push({ label: label || undefined, url: url || undefined });
      }
      continue;
    }
    const label = plainTextSanitize(entry);
    if (label) {
      mapped.push({ label });
    }
  }
  return mapped;
};

const normalizedCitations = (
  cite: CreativeWorksCatalogCiteThisRaw | undefined
): CreativeWorksCitations | undefined => {
  if (!cite) {
    return undefined;
  }
  const record = cite;
  const citations: CreativeWorksCitations = {};
  for (const key of ['apa', 'chicago', 'mla'] as const) {
    const value = plainTextSanitize(record[key]);
    if (value) {
      citations[key] = value;
    }
  }
  return Object.keys(citations).length > 0 ? citations : undefined;
};

const primaryDescription = (
  item: CreativeWorksCatalogItemRaw,
  nested: CreativeWorksCatalogItemRaw | undefined
): string => {
  const fromDescription = firstPlainLine(toArray(item.description));
  if (fromDescription) {
    return fromDescription;
  }
  const fromSummary = plainTextSanitize(nested?.summary ?? item.summary);
  return fromSummary;
};

const notesLines = (item: CreativeWorksCatalogItemRaw): string[] => {
  const rows = plainTextSanitizeArray(
    Array.isArray(item.notes) ? item.notes : []
  );
  const rights = plainTextSanitizeArray(
    Array.isArray(item.rights) ? item.rights : []
  ).flatMap((block) =>
    block
      .split(/\n{2,}/)
      .map((paragraph) => paragraph.trim())
      .filter(Boolean)
  );
  return [...rows, ...rights].filter(
    (line, index, all) => all.indexOf(line) === index
  );
};

const mediumLines = (
  item: CreativeWorksCatalogItemRaw,
  nested: CreativeWorksCatalogItemRaw | undefined
): string[] => {
  const rows = plainTextSanitizeArray(
    Array.isArray(item.medium) ? item.medium : []
  );
  if (rows.length > 0) {
    return rows;
  }

  const extras: string[] = [];
  const runningTime = plainTextSanitize(
    item.running_time ?? nested?.running_time
  );
  const videoType = plainTextSanitize(item.video_type ?? nested?.video_type);
  if (runningTime) {
    extras.push(runningTime);
  }
  if (videoType) {
    extras.push(videoType);
  }

  const mime = Array.isArray(item.mime_type)
    ? item.mime_type
        .map((entry) => String(entry).trim())
        .filter(Boolean)
        .join(', ')
    : '';
  if (mime) {
    extras.push(mime);
  }

  if (item.duration != null && item.duration !== '') {
    extras.push(`Duration: ${item.duration}s`);
  }

  return extras;
};

const publishedByLine = (
  item: CreativeWorksCatalogItemRaw,
  nested: CreativeWorksCatalogItemRaw | undefined,
  createdPublishedRows: string[]
): string =>
  createdPublishedRows[0] ||
  plainTextSanitize(item.event_date ?? nested?.event_date) ||
  '';

const normalizedLanguageLabels = (
  item: CreativeWorksCatalogItemRaw
): string[] => {
  const out: string[] = [];

  if (Array.isArray(item.language)) {
    for (const entry of item.language) {
      if (typeof entry === 'string') {
        const label = plainTextSanitize(entry);
        if (label) {
          out.push(label);
        }
      }
    }
  }

  if (Array.isArray(item.languages)) {
    for (const entry of item.languages) {
      if (typeof entry === 'string') {
        const label = plainTextSanitize(entry);
        if (label) {
          out.push(label);
        }
      } else if (entry && typeof entry === 'object') {
        for (const key of Object.keys(entry as Record<string, unknown>)) {
          const label = plainTextSanitize(key);
          if (label) {
            out.push(label);
          }
        }
      }
    }
  }

  return Array.from(new Set(out));
};

const primaryFormat = (item: CreativeWorksCatalogItemRaw): string => {
  const original = joinFormatFragments(item.original_format);
  if (original) {
    return original;
  }
  const format = joinFormatFragments(item.format);
  if (format) {
    return format;
  }
  const online = joinFormatFragments(item.online_format);
  if (online) {
    return online;
  }
  return 'unknown';
};

const searchCardFormatLabel = (row: CreativeWorksCatalogResultRaw): string => {
  const onlineFormats = Array.isArray(row.online_format)
    ? row.online_format.map((entry) => String(entry).trim().toLowerCase())
    : [];

  for (const label of ONLINE_FORMAT_ORDER) {
    if (onlineFormats.includes(label)) {
      return label;
    }
  }

  const fromOriginal = joinFormatFragments(row.original_format);
  if (fromOriginal) {
    return fromOriginal.toLowerCase();
  }
  const fromFormat = joinFormatFragments(row.format);
  if (fromFormat) {
    return fromFormat.toLowerCase();
  }
  return 'unknown';
};

const searchCardContributors = (row: CreativeWorksCatalogResultRaw): string => {
  const raw = row.contributor ?? row.contributors;
  if (!raw) {
    return '';
  }

  const names: string[] = [];
  if (typeof raw === 'string') {
    const name = raw.trim();
    return name ? plainTextSanitize(name) : '';
  }
  if (Array.isArray(raw)) {
    for (const entry of raw) {
      if (typeof entry === 'string') {
        const name = entry.trim();
        if (name) {
          names.push(name);
        }
      } else if (entry && typeof entry === 'object') {
        const record = entry as Record<string, unknown>;
        if (record.title || record.name) {
          const name = String(record.title || record.name).trim();
          if (name) {
            names.push(name);
          }
        } else {
          names.push(...Object.keys(record));
        }
      }
    }
  } else if (raw && typeof raw === 'object') {
    names.push(...Object.keys(raw as Record<string, unknown>));
  }

  return plainTextSanitize(names.join(', '));
};

/**
 * Maps one catalog search result row to a result card.
 * @param result - Raw search hit from catalog catalog search.
 * @returns Card for the results grid, or `null` when `url`/`id` is missing.
 */
export const mapCatalogResultToCreativeWorksCard = (
  result: CreativeWorksCatalogResultRaw
): CreativeWorksSearchCard | null => {
  const rawId = String(result.url || result.id || '').trim();
  if (!rawId) {
    return null;
  }

  const catalogItemId = extractItemIdFromSearchRow(result);
  const cardId = catalogItemId || rawId;
  const catalogUrl = urlAbsolutize(rawId);
  const link = catalogUrl || rawId;

  return {
    id: cardId,
    link,
    format: searchCardFormatLabel(result),
    title: formatCreativeWorksSearchCardTitle(
      plainTextSanitize(result.title) || rawId
    ),
    description: plainTextSanitize(
      Array.isArray(result.description)
        ? result.description.find(Boolean)
        : result.description
    ),
    date: extractCopyrightRegistrationDate(result),
    contributors: searchCardContributors(result),
    image_url: firstAbsolutizedImage(result.image_url) || undefined,
    catalog_url: catalogUrl || undefined,
  };
};

/**
 * Maps search rows to cards, omitting invalid rows.
 * @param results - Raw catalog search `results` array.
 * @returns Non-null {@link CreativeWorksSearchCard} entries only.
 */
export const mapCatalogResultsToCreativeWorksCards = (
  results: CreativeWorksCatalogResultRaw[] | undefined
): CreativeWorksSearchCard[] =>
  (results || [])
    .map(mapCatalogResultToCreativeWorksCard)
    .filter((card): card is CreativeWorksSearchCard => card !== null);

const simplifiedRelatedCard = (
  row: CreativeWorksCatalogResultRaw
): CreativeWorksSearchCard | null => mapCatalogResultToCreativeWorksCard(row);

/**
 * Maps a catalog item JSON response (`?fo=json`) to the full detail view model.
 * @param response - Parsed catalog item API payload including `item`, `resources`, and related lists.
 * @param requestedId - Id or URL from the route (used for links and fallback fields).
 * @returns {@link CreativeWorksSearchItem}, or `null` when `item` is missing.
 */
export const mapLocItemResponseToDetail = (
  response: CreativeWorksCatalogItemApiResponse,
  requestedId: string
): CreativeWorksSearchItem | null => {
  const item = response.item;
  if (!item) {
    return null;
  }

  const nested = item.item;

  const contributors = normalizeContributors(
    item.contributors ?? item.contributor
  );
  const resources = normalizeResources(response, item);
  const otherFormats = normalizedOtherFormats(item);
  const citations = normalizedCitations(response.cite_this);
  const subjectsMerged = mergeSubjects(item);
  const createdPublishedRows = plainTextSanitizeArray(
    toArray(item.created_published)
  );
  const callNumberRows = plainTextSanitizeArray(
    Array.isArray(item.call_number)
      ? item.call_number
      : item.call_number
      ? [item.call_number]
      : []
  );
  const resourcePreview = resources.slice(0, MAX_RESOURCE_PREVIEW);
  const lccn = controlNumberFromCatalogItem(
    item as unknown as Record<string, unknown>
  );

  const catalogUrl = requestedId.includes('://')
    ? urlAbsolutize(requestedId)
    : catalogItemUrl(requestedId);

  return {
    id: requestedId,
    catalog_url: catalogUrl,
    title: plainTextSanitize(item.title),
    format: primaryFormat(item),
    image_url: firstAbsolutizedImage(item.image_url) || undefined,
    description: primaryDescription(item, nested),
    date: extractCopyrightRegistrationDate(item),
    contributors: contributors
      .map((contributor) => contributor.name)
      .filter(Boolean)
      .join(', '),
    notes: notesLines(item),
    medium: mediumLines(item, nested),
    published_by: publishedByLine(item, nested, createdPublishedRows),
    identification: {
      control_number: lccn || undefined,
      shelf_id: plainTextSanitize(item.shelf_id) || null,
      call_numbers: callNumberRows,
      number_identifiers: plainTextSanitizeArray(toArray(item.number)),
    },
    digital_content: {
      resources,
      other_formats: otherFormats,
    },
    additional_info: {
      citation: citations,
      resources: resourcePreview,
    },
    access_restricted: item.access_restricted === true,
    digitized: item.digitized === true,
    control_number: lccn || undefined,
    call_numbers: callNumberRows,
    contributor_details: contributors,
    created_published: createdPublishedRows,
    subject_headings: plainTextSanitizeArray(
      Array.isArray(item.subject_headings) ? item.subject_headings : []
    ),
    subjects: subjectsMerged,
    languages: normalizedLanguageLabels(item),
    citations,
    resources_preview: resourcePreview,
    resources,
    other_formats: otherFormats,
    more_like_this: (response.more_like_this || [])
      .map(simplifiedRelatedCard)
      .filter((card): card is CreativeWorksSearchCard => card !== null),
    related_items: (response.related_items || [])
      .map(simplifiedRelatedCard)
      .filter((card): card is CreativeWorksSearchCard => card !== null),
  };
};

/**
 * Builds the catalog item API URL that returns JSON (`fo=json`).
 * @param itemId - catalog catalog item id.
 * @returns Absolute URL for `fetchCatalogJson` item detail requests.
 */
export const buildCatalogItemDetailUrl = (itemId: string): string => {
  const params = new URLSearchParams({
    fo: 'json',
    at: CREATIVE_WORKS_SEARCH_ITEM_AT,
  });
  return `${catalogItemUrl(itemId)}?${params.toString()}`;
};

/** Re-exported parse helpers used by item resolution in {@link creativeWorksSearchData}. */
export {
  extractItemIdFromSearchRow,
  extractItemIdFromString,
  isSafeCreativeWorksSearchItemId,
} from './creativeWorksSearchParseUtils';
